/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  Gauge,
  LineChart,
  Loader2,
  Mail,
  SearchCheck,
  ShieldCheck,
} from "lucide-react";
import PaxiaLogo from "../../branding/logo";
import homepageContent from "../../content/homepageContent";

const compactSectionClass = "landing-section px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-20";

export default function CompactHomepage({ content = homepageContent }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const utm = useMemo(() => {
    if (typeof window === "undefined") {
      return { source: "", medium: "", campaign: "", content: "" };
    }

    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get("utm_source") || "",
      medium: params.get("utm_medium") || "",
      campaign: params.get("utm_campaign") || "",
      content: params.get("utm_content") || "homepage-compact",
    };
  }, []);

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!isEmailValid) {
      setError("Please enter a valid email.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, ...utm }),
      });

      if (!response.ok) {
        throw new Error("Submission failed. Try again.");
      }

      setMessage("Thanks - you're on the list.");
      setEmail("");
    } catch (submissionError) {
      setError(submissionError.message || "Submission failed. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="landing-shell min-h-screen overflow-x-hidden">
      <CompactHero content={content} />
      <CompactProblem content={content} />
      <CompactWorkflow content={content} />
      <CompactProductProof content={content} />
      <CompactAiAutomation content={content} />
      <CompactSignup
        content={content}
        email={email}
        error={error}
        isEmailValid={isEmailValid}
        loading={loading}
        message={message}
        onEmailChange={setEmail}
        onSubmit={handleSubmit}
      />
      <CompactFooter content={content} />
    </main>
  );
}

function CompactHero({ content }) {
  const { brand, hero } = content;

  return (
    <header className="landing-hero">
      <nav className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <a href="#top" className="flex items-center gap-3" aria-label={`${brand.name} home`}>
          <img src={PaxiaLogo} alt={brand.name} className="h-12 w-auto sm:h-[3.75rem]" />
        </a>

        <div className="hidden items-center gap-7 text-sm text-slate-300 md:flex">
          <a href="#how-it-works" className="transition-colors hover:text-[#00D5FF]">How it works</a>
          <a href="#product" className="transition-colors hover:text-[#00D5FF]">Product</a>
          <a href="#ai-automation" className="transition-colors hover:text-[#00D5FF]">AI</a>
          <a href="#signup" className="transition-colors hover:text-[#00D5FF]">Beta</a>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={hero.secondaryCta.href}
            className="hidden rounded-[8px] px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white sm:inline-flex"
          >
            {hero.secondaryCta.label}
          </Link>
          <a
            href={hero.primaryCta.href}
            className="brand-button px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00D5FF] focus:ring-offset-2 focus:ring-offset-[#05070D]"
          >
            {hero.primaryCta.label}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </nav>

      <section id="top" className="relative z-10 mx-auto grid max-w-7xl gap-10 px-4 pb-14 pt-10 sm:px-6 sm:pb-16 sm:pt-14 lg:grid-cols-[0.92fr_1.08fr] lg:px-8 lg:pb-20 lg:pt-16">
        <div className="flex flex-col justify-center">
          <p className="landing-eyebrow mb-5 px-3 py-1 text-xs font-semibold uppercase">
            {hero.eyebrow}
          </p>
          <h1 className="brand-text-gradient max-w-4xl text-5xl font-semibold leading-[1.02] sm:text-6xl lg:text-7xl">
            {hero.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
            Connect accounts, set guardrails, trade from one calm workspace, and turn repeatable routines into supervised AI workflows.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href={hero.primaryCta.href}
              className="brand-button px-5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#00D5FF] focus:ring-offset-2 focus:ring-offset-[#05070D]"
            >
              {hero.primaryCta.label}
              <ArrowRight className="h-5 w-5" />
            </a>
            <Link
              to={hero.secondaryCta.href}
              className="secondary-button px-5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-[#05070D]"
            >
              {hero.secondaryCta.label}
            </Link>
          </div>

          <ul className="mt-8 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
            {hero.trustLine.map((item) => (
              <li key={item} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 flex-none text-[#00D5FF]" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <CompactStudioPreview content={content} />
      </section>
    </header>
  );
}

function CompactStudioPreview({ content }) {
  const { hero } = content;

  return (
    <div className="relative self-center">
      <div className="soft-panel p-2 sm:p-3">
        <div className="overflow-hidden rounded-[8px] border border-white/10 bg-[#070B13]/90">
          <div className="h-1 bg-[linear-gradient(90deg,#0B3DFF,#00D5FF,#6D5BFF)]" />
          <div className="grid gap-3 p-4 sm:grid-cols-[0.86fr_1.14fr]">
            <div className="space-y-3">
              <div className="landing-panel p-4">
                <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-300">
                  <ShieldCheck className="h-4 w-4 text-[#00D5FF]" />
                  {hero.studioPreview.account.label}
                </div>
                <p className="text-lg font-semibold text-white">{hero.studioPreview.account.detail}</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">Rules are checked before new action reaches the broker.</p>
              </div>

              <div className="landing-panel p-4">
                <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-300">
                  <Gauge className="h-4 w-4 text-[#00D5FF]" />
                  Guardrails
                </div>
                <div className="space-y-2">
                  {hero.studioPreview.risk.map((item) => (
                    <div key={item.label} className="flex items-center justify-between rounded-[8px] border border-white/10 bg-white/[0.03] px-3 py-2">
                      <span className="text-sm text-slate-400">{item.label}</span>
                      <span className="text-sm font-semibold text-white">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="landing-panel p-4">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-300">
                <LineChart className="h-4 w-4 text-[#00D5FF]" />
                Studio queue
              </div>
              <div className="chart-surface mb-4 h-44 p-4">
                <div className="flex h-full items-end gap-2">
                  {[38, 54, 42, 68, 58, 76, 62, 84].map((height, index) => (
                    <div key={`${height}-${index}`} className="flex h-full flex-1 items-end">
                      <div
                        className={`chart-bar w-full rounded-sm ${index % 2 === 0 ? "bg-[#00D5FF]/80" : "bg-[#6D5BFF]/70"}`}
                        style={{ height: `${height}%`, "--bar-delay": `${index * 90}ms` }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                {hero.studioPreview.activity.map((item) => (
                  <div key={item.label} className="flex items-center justify-between gap-3 rounded-[8px] border border-white/10 bg-white/[0.03] px-3 py-2">
                    <span className="text-sm text-slate-400">{item.label}</span>
                    <span className="text-right text-sm font-semibold text-cyan-100">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompactProblem({ content }) {
  const { problem } = content;

  return (
    <section id={problem.id} className={`${compactSectionClass} landing-section-wash`}>
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 max-w-3xl">
          <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">{problem.label}</p>
          <h2 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">A good plan can still get scattered under pressure.</h2>
          <p className="mt-4 text-base leading-7 text-slate-300">
            Accounts, charts, news, sizing, notes, and review often live in separate tools. Paxia.ai brings the decision back into one controlled studio.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {problem.items.map((item) => (
            <article key={item.title} className="story-card">
              <h3 className="text-lg font-semibold text-white">{item.title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-300">{item.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CompactWorkflow({ content }) {
  const { workflow } = content;

  return (
    <section id="how-it-works" className={`${compactSectionClass} landing-section-panel`}>
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">{workflow.label}</p>
          <h2 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">Keep the trading day moving in order.</h2>
          <p className="mt-4 text-base leading-7 text-slate-300">
            The compact version makes the product story a sequence, not a feature inventory.
          </p>
          <div className="brand-divider mx-auto mt-6 max-w-sm" />
        </div>

        <ol className="workflow-rail grid gap-3 md:grid-cols-2 lg:grid-cols-5">
          {workflow.steps.map((step, index) => (
            <li key={step.title} className="workflow-stage p-5">
              <div className="mb-4 flex items-center justify-between gap-4">
                <div className="icon-tile">
                  <span className="font-mono text-xs font-semibold">{String(index + 1).padStart(2, "0")}</span>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-white">{step.title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-400">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function CompactProductProof({ content }) {
  const { decisionLoop, riskControls, studio } = content;
  const cards = [
    {
      icon: ShieldCheck,
      title: "Risk rules before action",
      body: riskControls.body,
      points: riskControls.features.slice(0, 4),
    },
    {
      icon: LineChart,
      title: "A studio that knows the account",
      body: studio.body,
      points: ["Account state stays visible", "Chart-led execution", "Layouts and widgets"],
    },
    {
      icon: SearchCheck,
      title: "Context before and after the trade",
      body: decisionLoop.body,
      points: decisionLoop.lanes.map((lane) => lane.title),
    },
  ];

  return (
    <section id="product" className={compactSectionClass}>
      <div className="mx-auto max-w-7xl">
        <div className="mb-10 max-w-3xl">
          <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">Product spine</p>
          <h2 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">Risk, execution, research, and review in one loop.</h2>
          <p className="mt-4 text-base leading-7 text-slate-300">
            The short version keeps the proof close to the workflow: set the rules, work from the studio, add context, and close the loop.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <article key={card.title} className="ai-capability p-5 sm:p-6">
                <div className="icon-tile mb-5 h-11 w-11 rounded-full">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-white">{card.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-300">{card.body}</p>
                <ul className="mt-5 space-y-2">
                  {card.points.map((point) => (
                    <li key={point} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="h-4 w-4 flex-none text-[#00D5FF]" />
                      {point}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CompactAiAutomation({ content }) {
  const { aiTools, automation } = content;
  const leadExample = aiTools.examples[0];

  return (
    <section id="ai-automation" className={`${compactSectionClass} landing-section-mesh`}>
      <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">AI and automation</p>
          <h2 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">Turn repeatable routines into supervised workflows.</h2>
          <p className="mt-4 text-base leading-7 text-slate-300">
            Describe the research, monitoring, sizing, or review routine. Paxia.ai can shape it into a workflow that reports, alerts, requests review, or runs inside configured limits.
          </p>
          <p className="automation-note mt-6 p-5 text-sm leading-6 text-cyan-50">
            {automation.trustCopy}
          </p>
        </div>

        <div className="builder-canvas p-5 sm:p-6">
          <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-cyan-50">
            <Bot className="h-4 w-4" />
            Example workflow
          </div>
          <h3 className="text-2xl font-semibold leading-tight text-white">{leadExample.title}</h3>
          <p className="mt-4 text-sm leading-7 text-slate-300">{leadExample.body}</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {automation.ladder.slice(0, 4).map((step, index) => (
              <div key={step.title} className="system-step grid grid-cols-[auto_1fr] gap-3 p-4">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0B3DFF]/20 font-mono text-xs font-semibold text-[#00D5FF]">
                  {index + 1}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">{step.title}</span>
                  <span className="mt-1 block text-sm leading-6 text-slate-400">{step.body}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CompactSignup({ content, email, error, isEmailValid, loading, message, onEmailChange, onSubmit }) {
  const { finalCta, trust } = content;

  return (
    <section id={finalCta.id} className="landing-section landing-cta px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="cta-stage p-6 sm:p-8 lg:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr]">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.24em] text-[#8BE9FF]">Beta access</p>
              <h2 className="max-w-3xl text-4xl font-semibold leading-[1.04] text-white sm:text-5xl">Build your trading process inside one controlled studio.</h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">{finalCta.body}</p>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {trust.points.slice(0, 4).map((point) => (
                  <li key={point} className="cta-checkitem flex items-center gap-3 text-sm text-slate-100">
                    <CheckCircle2 className="h-4 w-4 flex-none text-[#00D5FF]" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div className="cta-form-shell p-5 sm:p-6" aria-live="polite">
              <div className="mb-5">
                <p className="text-sm font-semibold text-white">Request beta access</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">Tell us where to reach you. We will use this only for beta updates.</p>
              </div>

              {message ? (
                <div className="cta-success p-6 text-center" role="status">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-[#00D5FF]" />
                  <p className="mt-3 text-lg font-semibold text-white">{message}</p>
                  <p className="mt-2 text-sm text-emerald-50">We received your submission and will be in touch.</p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid gap-3" aria-live="polite">
                  <label className="sr-only" htmlFor="compact-homepage-email">
                    Email
                  </label>
                  <input
                    id="compact-homepage-email"
                    type="email"
                    required
                    value={email}
                    onChange={(event) => onEmailChange(event.target.value)}
                    placeholder="you@domain.com"
                    className="min-h-12 rounded-[8px] border border-white/15 bg-[#05070D]/90 px-4 py-3 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-[#00D5FF] focus:ring-2 focus:ring-[#00D5FF]/20"
                  />
                  <button
                    type="submit"
                    disabled={loading || !isEmailValid}
                    className="brand-button min-h-12 px-5 py-3 disabled:cursor-not-allowed disabled:bg-slate-600 disabled:text-slate-300 disabled:shadow-none"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
                    {loading ? "Submitting..." : finalCta.primaryCta.label}
                  </button>
                </form>
              )}

              {error && <p className="mt-3 text-sm text-rose-200">{error}</p>}

              <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-slate-400">
                <span>No spam. No hype.</span>
                <Link to={finalCta.secondaryCta.href} className="text-cyan-100 transition-colors hover:text-white">
                  {finalCta.secondaryCta.label}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CompactFooter({ content }) {
  const { brand, footer } = content;

  return (
    <footer className="border-t border-white/10 bg-[#05070D] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 text-sm text-slate-400 md:flex-row md:items-start md:justify-between">
        <div className="max-w-2xl">
          <img src={PaxiaLogo} alt={brand.name} className="h-10 w-auto" />
          <p className="mt-4 leading-6">{footer.disclaimer}</p>
          <p className="mt-3">© {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
        </div>
        <div className="flex flex-wrap gap-4 md:justify-end">
          {footer.links.map((link) =>
            link.href.startsWith("/") ? (
              <Link key={link.href} to={link.href} className="transition-colors hover:text-[#00D5FF]">
                {link.label}
              </Link>
            ) : (
              <a key={link.href} href={link.href} className="transition-colors hover:text-[#00D5FF]">
                {link.label}
              </a>
            ),
          )}
          <a href={`mailto:${footer.contactEmail}`} className="transition-colors hover:text-[#00D5FF]">
            {footer.contactEmail}
          </a>
        </div>
      </div>
    </footer>
  );
}
